import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { LiveDot } from './UssdPanel';
import askSuggestions from '../data/ask';
import { useSiteChat } from '../contexts/ChatContext';

const TEASER_KEY = 'ask-teaser-dismissed';
const opener = askSuggestions[0];

function teaserAlreadySeen() {
  try {
    return localStorage.getItem(TEASER_KEY) === '1';
  } catch (err) {
    return false;
  }
}

/**
 * The launcher reads as a USSD short code rather than a generic chat bubble —
 * people scroll past bubbles, but a live green dot and the word "ask" say what
 * is actually on offer.
 *
 * The nudge above it appears once per visitor, only after they have read far
 * enough to have a question, and never again once dismissed or once the chat
 * has been opened.
 */
function ChatLauncher({ onOpen, disabled }) {
  const { ask, hasOpened } = useSiteChat();
  const [showTeaser, setShowTeaser] = useState(false);

  useEffect(() => {
    if (hasOpened || teaserAlreadySeen()) return undefined;

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 400 && window.scrollY / scrollable > 0.5) setShowTeaser(true);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasOpened]);

  const dismissTeaser = () => {
    setShowTeaser(false);
    try {
      localStorage.setItem(TEASER_KEY, '1');
    } catch (err) {
      /* storage unavailable — the teaser simply reappears next visit */
    }
  };

  return (
    <div className="fixed right-5 bottom-5 z-[1000000] flex flex-col items-end gap-3">
      {showTeaser && !hasOpened && (
        <div className="w-[17.5rem] max-w-[calc(100vw-2.5rem)] animate-rise rounded-sm border border-black/40 bg-panel p-4 font-mono text-xs text-panel-foreground shadow-[0_12px_32px_rgba(0,0,0,0.28)] dark:border-white/10">
          <div className="flex items-center justify-between text-eyebrow uppercase tracking-[0.18em] text-panel-muted">
            <span className="flex items-center gap-1.5">
              <LiveDot />
              Live
            </span>
            <button
              type="button"
              onClick={dismissTeaser}
              aria-label="Dismiss"
              className="-m-1 rounded-sm p-1 text-panel-muted transition-colors hover:text-panel-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <p className="mt-3 leading-relaxed">Still curious? Ask my assistant.</p>

          <button
            type="button"
            onClick={() => {
              dismissTeaser();
              ask(opener);
            }}
            className="mt-3 flex w-[calc(100%+1rem)] items-baseline gap-2 -mx-2 rounded-sm px-2 py-1.5 text-left leading-relaxed transition-colors hover:bg-white/[0.07]"
          >
            <span className="text-panel-signal">&rsaquo;</span>
            <span>{opener}</span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        aria-label="Ask my assistant"
        className="group flex h-11 items-center gap-2.5 rounded-sm border border-black/40 bg-panel px-4 font-mono text-xs uppercase tracking-[0.18em] text-panel-foreground shadow-[0_8px_24px_rgba(0,0,0,0.24)] transition-colors hover:bg-[hsl(135_10%_14%)] disabled:cursor-wait disabled:opacity-70 dark:border-white/10"
      >
        <LiveDot />
        <span>Ask</span>
        <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 group-hover:max-w-[9rem] group-hover:opacity-100 group-focus-visible:max-w-[9rem] group-focus-visible:opacity-100">
          me anything
        </span>
      </button>
    </div>
  );
}

export default ChatLauncher;
